import { useEffect } from "react";

/**
 * The thing you just paid for, said out loud.
 *
 * A bought clue still lands in the Revealed list and a guess still lands in the
 * guess log — those are the record. This is the moment: it appears where the
 * eye already is, then gets out of the way. Without it the answer arrives
 * somewhere the player has to go looking for, which is the opposite of a
 * reward.
 */
export type ToastData = {
  /** Bumping this re-triggers the animation for a repeat of the same content. */
  id: number;
  icon: string;
  label: string;
  value: string;
  sub?: string;
  /** Brand colour that carries the meaning: Beacon for clues, ramp for guesses. */
  tint: string;
};

export function Toast({
  toast,
  onDone,
  ms = 3600,
}: {
  toast: ToastData | null;
  onDone: () => void;
  ms?: number;
}) {
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(onDone, ms);
    return () => clearTimeout(timer);
  }, [toast, ms, onDone]);

  if (!toast) return null;

  return (
    <div className="toast-wrap" aria-live="polite">
      <button
        key={toast.id}
        className="toast"
        style={{ borderColor: toast.tint }}
        onClick={onDone}
        aria-label={`${toast.label}: ${toast.value}. Dismiss.`}
      >
        <span className="toast-icon" style={{ background: `${toast.tint}26` }}>
          {toast.icon}
        </span>
        <span className="toast-body">
          <span className="toast-label">{toast.label}</span>
          <span className="toast-value" style={{ color: toast.tint }}>
            {toast.value}
          </span>
          {toast.sub && <span className="toast-sub">{toast.sub}</span>}
        </span>
      </button>
    </div>
  );
}

/**
 * A clue arriving is an event, not a row appearing in a list. This is the
 * full-width beat: the icon lands, the hider says one line, and it clears
 * itself before it outstays its welcome.
 */
export function ClueEvent({
  event,
  onDone,
  ms = 3200,
}: {
  event: { id: number; icon: string; text: string; index: number } | null;
  onDone: () => void;
  ms?: number;
}) {
  useEffect(() => {
    if (!event) return;
    const timer = setTimeout(onDone, ms);
    return () => clearTimeout(timer);
  }, [event, ms, onDone]);

  if (!event) return null;

  return (
    <div className="clue-event-wrap" onClick={onDone}>
      <div className="clue-event" key={event.id} role="status">
        <div className="clue-event-tag">Clue {event.index} unlocked</div>
        <div className="clue-event-icon">{event.icon}</div>
        <div className="clue-event-text">{event.text}</div>
      </div>
    </div>
  );
}
